import { useCallback, useRef } from "react";
import type { RowData } from "@tanstack/react-table";
import { resolveColumnMeta } from "../../columns/columnKinds";
import { isSameCellAddress } from "../../focus/navigation";
import type { DataGridCellAddress, DataGridInstance } from "../../types";
import type { EditContext } from "../editActions";
import { openListEditor } from "./listEditActions";

/**
 * Whether a click on the cell box opens the column's editor: a popup editor's
 * trigger spans the cell, while an inline one (`col.edit.text`) wraps only its
 * text and handles its own clicks, so the rest of the cell opens the row.
 */
const shouldCellBoxOpenEditor = <TData extends RowData>(
    instance: DataGridInstance<TData>,
    columnId: string,
) => {
    const column = instance.table.getColumn(columnId);
    return (
        column !== undefined &&
        resolveColumnMeta(column.columnDef, columnId).editorPopup !== undefined
    );
};

/**
 * List editing's part of the grid's click: a click on a list-editable cell
 * opens its editor. The popover closes itself on a click outside it before
 * the grid sees that click, so the editor open at the press is remembered,
 * and a click on that editor's own cell leaves it closed.
 */
export const useListCellClick = <TData extends RowData>({
    instance,
    controller,
}: EditContext<TData>) => {
    const pressedEditorRef = useRef<DataGridCellAddress | null>(null);

    const onPointerDown = useCallback(() => {
        pressedEditorRef.current = instance.atoms.activeEditor.get();
    }, [instance]);

    /** Returns true when the click belongs to list editing, not the row. */
    const clickCell = useCallback(
        (address: DataGridCellAddress) => {
            const pressedEditor = pressedEditorRef.current;
            pressedEditorRef.current = null;
            if (
                instance.hasListEditing &&
                pressedEditor !== null &&
                isSameCellAddress(pressedEditor, address)
            ) {
                return true;
            }
            return (
                shouldCellBoxOpenEditor(instance, address.columnId) &&
                openListEditor({ instance, controller }, address)
            );
        },
        [controller, instance],
    );

    return { onPointerDown, clickCell };
};
